/**
 * Dashboard Page Service
 * Handles CRUD operations for dashboard pages/layouts
 * Supports both personal and shared pages
 */

import axios from 'axios';

class DashboardPageService {
  /**
   * Get all dashboard pages (personal + shared)
   */
  async getPages() {
    try {
      const response = await axios.get('/api/user-layouts');
      return response.data;
    } catch (error) {
      console.error('Error fetching dashboard pages:', error);
      throw error;
    }
  }

  /**
   * Get a specific page with all component instances
   */
  async getPage(pageId) {
    try {
      const response = await axios.get(`/api/user-layouts/${pageId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching dashboard page ${pageId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new dashboard page
   */
  async createPage({ layoutName, description, isDefault = false, isShared = false, tabsConfig = [] }) {
    try {
      const response = await axios.post('/api/user-layouts', {
        layoutName,
        description,
        isDefault,
        isShared,
        tabsConfig
      });
      return response.data;
    } catch (error) {
      console.error('Error creating dashboard page:', error);
      throw error;
    }
  }

  /**
   * Update an existing dashboard page
   */
  async updatePage(pageId, { layoutName, description, tabsConfig }) {
    try {
      const response = await axios.put(`/api/user-layouts/${pageId}`, {
        layoutName,
        description,
        tabsConfig
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating dashboard page ${pageId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a dashboard page
   */
  async deletePage(pageId) {
    try {
      const response = await axios.delete(`/api/user-layouts/${pageId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting dashboard page ${pageId}:`, error);
      throw error;
    }
  }

  /**
   * Share or unshare a page (Manager/Admin only)
   */
  async sharePage(pageId, isShared) {
    try {
      const response = await axios.put(`/api/user-layouts/${pageId}/share`, {
        isShared
      });
      return response.data;
    } catch (error) {
      console.error(`Error sharing/unsharing page ${pageId}:`, error);
      throw error;
    }
  }

  /**
   * Set a page as default
   */
  async setDefaultPage(pageId) {
    try {
      const response = await axios.put(`/api/user-layouts/${pageId}/default`);
      return response.data;
    } catch (error) {
      console.error(`Error setting default page ${pageId}:`, error);
      throw error;
    }
  }

  /**
   * Add a component to a page
   */
  async addComponent(pageId, { componentId, tabId, position, customization }) {
    try {
      const response = await axios.post(`/api/user-layouts/${pageId}/add-component`, {
        componentId,
        tabId,
        position,
        customization
      });
      return response.data;
    } catch (error) {
      console.error(`Error adding component to page ${pageId}:`, error);
      throw error;
    }
  }

  /**
   * Update a component instance
   */
  async updateComponent(instanceId, { position, customization }) {
    try {
      const response = await axios.put(`/api/user-layouts/instances/${instanceId}`, {
        position,
        customization
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating component instance ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Remove a component from a page
   */
  async removeComponent(instanceId) {
    try {
      const response = await axios.delete(`/api/user-layouts/instances/${instanceId}`);
      return response.data;
    } catch (error) {
      console.error(`Error removing component instance ${instanceId}:`, error);
      throw error;
    }
  }

  /**
   * Reorder components within a tab
   */
  async reorderComponents(pageId, tabId, instanceIds) {
    try {
      const response = await axios.put(`/api/user-layouts/${pageId}/reorder-components`, {
        tabId,
        instanceIds
      });
      return response.data;
    } catch (error) {
      console.error(`Error reordering components in page ${pageId}:`, error);
      throw error;
    }
  }

  /**
   * Helper: Check if user has Manager or Admin role
   */
  hasManagerOrAdminRole(user) {
    if (!user) return false;

    // Check if user is system admin
    if (user.userType === 'admin' || user.isAdmin || user.isSuperAdmin) {
      return true;
    }

    // Check roles array for Manager or Admin role
    if (Array.isArray(user.roles)) {
      return user.roles.some(role => {
        const roleName = typeof role === 'object' ? (role.RoleName || role.name) : role;
        return roleName === 'Manager' || roleName === 'Admin' || roleName === 'SuperAdmin';
      });
    }

    return false;
  }

  /**
   * Helper: Categorize pages into personal and shared
   */
  categorizPages(pages, currentUserId) {
    const personal = [];
    const shared = [];

    pages.forEach(page => {
      if (page.IsShared) {
        shared.push(page);
      } else if (page.UserID === currentUserId) {
        personal.push(page);
      }
    });

    return { personal, shared };
  }
}

export default new DashboardPageService();

